import { Button } from '@readcloud/component-library/components/Base/Button';

//import { ButtonWithOverlay } from '@readcloud/common-ui';
import { ToolType } from '@readcloud/annotations';
import React, { useRef } from 'react';
import { ConfirmDrawing } from '../../Modals/ConfirmDrawing/ConfirmDrawing';
import { DrawToolbar } from '../DrawToolbar/DrawToolbar';
import { SearchBar } from '../SearchBar/SearchBar';
import { ToolbarContainer } from '../ToolbarContainers/ToolbarContainer';
import useMediaQuery from '@readcloud/common/lib/useMediaQuery';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Annotation, User } from '@readcloud/data';
import {
  AnnotationsState,
  applicationActions,
  searchSelectors,
  useAppDispatch,
} from '@readcloud/state';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@readcloud/ui';
import C from 'classnames';
import {
  MdArrowBack,
  MdBookmark,
  MdChat,
  MdLink,
  MdMode,
  MdOutlineChromeReaderMode,
  MdPrint,
  MdSearch,
} from 'react-icons/md';
import { useSelector } from 'react-redux';
import {
  ALL_ANNOTATIONS,
  FILTERING_ANNOTATIONS,
} from '../../../types/TopToolbarConstants';
import { LoadingSection } from '../../Common/Base/LoadingSection';
import { trackEvent } from '@readcloud/common';

const searchBarContainerProps = {
  fullWidth: true,
} as const;

interface ITopToolbarProps {
  className?: string;
  isOpen?: boolean;
  pageNumber: number;
  disableDraw?: boolean;
  disableMedia?: boolean;
  disableHTML?: boolean;
  disableAnnotations?: boolean;
  disableBookmarks?: boolean;
  disableLink?: boolean;
  disableSearch?: boolean;
  title: string;
  onAnnotations: () => void;
  onBookmarks: () => void;
  goHome: () => void;
  onInfo?: () => void;
  onPrint?: () => void;
  onLink: () => void;
  onBeginDrawing?: (pageNumber: number) => void;
  onSearchOpen: () => void;
  currentlyDrawing?: AnnotationsState['currentlyDrawing'];
  addAnnotation: (anno: Annotation) => void;
  annotationButtonClassName?: string;
  bookmarksButtonClassName?: string;
  searchPanelClassName?: string;
  searchButtonClassName?: string;
  externalUrlButtonClassName?: string;
  homeButtonIcon?: React.ReactNode;
  tool: ToolType;
  onToolChange: (tool: ToolType) => void;
  onDiscardDrawing: () => void;
  authorId: string;
  bookId: string;
  currentlyFilteringText: string | undefined;
  saveAnnotationFilter: (annoFilter: string) => void;
  currentAuthor: string;
  filterUsers?: string[];
  setUserFilter: (users: string[]) => void;
  filterAnnotationBy: (filter: string, users: string[]) => void;
  knownUsers: User[];
  disableImmersive: boolean;
  getImmersiveToken?: () => Promise<any>;
  isOnline: boolean;
  checkOnline?: () => boolean;
  toggleIOSInterceptor?: (interceptState: 'on' | 'off') => void;
  printIOS?: (printHTML: string) => void;
  loadingPages?: boolean;
  customTextSelection?: boolean;
}

export const TopToolbar: React.FC<ITopToolbarProps> = ({
  className,
  isOpen,
  title,
  onAnnotations,
  onBookmarks,
  goHome,
  onInfo,
  onLink,
  onBeginDrawing,
  onSearchOpen,
  currentlyDrawing,
  annotationButtonClassName,
  bookmarksButtonClassName,
  searchPanelClassName,
  searchButtonClassName,
  externalUrlButtonClassName,
  disableBookmarks,
  disableAnnotations,
  disableSearch,
  disableLink,
  disableDraw,
  disableMedia,
  disableHTML,
  homeButtonIcon,
  tool,
  onToolChange,
  pageNumber,
  onDiscardDrawing,
  onPrint,
  addAnnotation,
  authorId,
  bookId,
  currentlyFilteringText,
  saveAnnotationFilter,
  currentAuthor,
  filterUsers,
  setUserFilter,
  knownUsers,
  filterAnnotationBy,
  disableImmersive,
  getImmersiveToken,
  isOnline,
  checkOnline,
  toggleIOSInterceptor,
  printIOS,
  loadingPages,
  customTextSelection,
}) => {
  const handleBeginDrawing = React.useCallback(() => {
    if (onBeginDrawing) {
      onBeginDrawing(pageNumber);
    }
  }, [pageNumber, onBeginDrawing]);

  const searchData = useSelector(searchSelectors.getSearchData);

  const [confirmDiscard] = React.useState(false);

  const [openConfirmation, setConfirmation] = React.useState<boolean>(false);

  const [immersiveText, setImmersiveText] = React.useState<string>('');

  const [immersiveReaderPopup, setImmersiveReaderPopup] =
    React.useState<React.ReactNode>(null);

  const [showTopToolbarPopover, setShowTopToolbarPopover] =
    React.useState<boolean>(false);

  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (checkOnline && immersiveText) {
      checkOnline();
    }
  }, [immersiveText]);

  const drawBtn = (
    <>
      <Button
        testId="topToolbarEnhancedAnnotationsButton"
        variant={'light'}
        icon
        onClick={() => {
          trackEvent('Webapp/TopToolbarEnhancedAnnotationsClicked', {
            bookTitle: title,
            bookId: bookId,
          });
          setShowTopToolbarPopover(false);
          if (
            currentlyFilteringText &&
            currentlyFilteringText.indexOf(FILTERING_ANNOTATIONS) >= 0 &&
            !filterUsers?.includes(currentAuthor)
          ) {
            setConfirmation(true);
          } else {
            handleBeginDrawing();
          }
        }}
        className="rc-w-full"
      >
        <MdMode size="28" />
      </Button>

      <ConfirmDrawing
        openDialog={openConfirmation}
        setOpen={setConfirmation}
        setFilter={saveAnnotationFilter}
        confirmAction={handleBeginDrawing}
        currentAuthor={currentAuthor}
        filterUsers={filterUsers}
        annoFilter={currentlyFilteringText || ''}
        setUserFilter={setUserFilter}
        knownUsers={knownUsers}
        filterAnnotationBy={filterAnnotationBy}
      />
    </>
  );

  const closeReader = () => {
    setImmersiveReaderPopup(undefined);
    toggleIOSInterceptor?.('on');
  };

  const handleImmersiveWorkbook = () => {
    if (isOnline) {
      dispatch(
        applicationActions.updateApplication({
          immersiveWorkbookModalOpen: true,
        })
      );
    } else {
      dispatch(
        applicationActions.updateApplication({
          offlineModalOpen: true,
        })
      );
    }
  };

  const buttons = (
    <>
      {(!disableDraw || !disableMedia || !disableHTML) && onBeginDrawing ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              testId="topToolbarEnhancedAnnotationsButton"
              variant="light"
              onClick={() => {
                trackEvent('Webapp/TopToolbarEnhancedAnnotationsClicked', {
                  bookTitle: title,
                  bookId: bookId,
                });
                setShowTopToolbarPopover(false);
                if (
                  currentlyFilteringText &&
                  currentlyFilteringText.indexOf(FILTERING_ANNOTATIONS) >= 0 &&
                  !filterUsers?.includes(currentAuthor)
                ) {
                  setConfirmation(true);
                } else {
                  handleBeginDrawing();
                }
              }}
              className="rc-flex rc-gap-5 rc-items-center rc-w-full rc-justify-start"
            >
              <MdMode className="rc-text-[18px] lg:rc-text-[28px]" />
              <span className="rc-block md:rc-hidden rc-text-xs">
                Enhanced Annotations
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="rc-hidden md:rc-block">
            Enhanced Annotations
          </TooltipContent>
        </Tooltip>
      ) : null}

      {!disableLink ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              testId="topToolbarCopyLinkButton"
              className={
                externalUrlButtonClassName +
                ' rc-w-full rc-flex rc-gap-5 rc-items-center rc-justify-start'
              }
              onClick={() => {
                setShowTopToolbarPopover(false);
                onLink();
              }}
              variant="light"
            >
              <MdLink className="rc-text-[18px] lg:rc-text-[28px]" />
              <span className="rc-block md:rc-hidden rc-text-xs">
                Copy Link
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="rc-hidden md:rc-block">
            Copy Link
          </TooltipContent>
        </Tooltip>
      ) : null}

      {!disableImmersive ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              testId="topToolbarImmersiveReaderButton"
              onClick={() => {
                trackEvent('Webapp/TopToolbarImmersiveReaderClicked', {
                  bookTitle: title,
                  bookId: bookId,
                });
                setShowTopToolbarPopover(false);
                handleImmersiveWorkbook();
              }}
              variant="light"
              className="rc-flex rc-gap-5 rc-items-center rc-justify-start rc-w-full"
            >
              <MdOutlineChromeReaderMode className="rc-text-[18px] lg:rc-text-[28px]" />
              <span className="rc-block md:rc-hidden rc-text-xs">
                Immersive Reader
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="rc-hidden md:rc-block">
            Immersive Reader
          </TooltipContent>
        </Tooltip>
      ) : null}

      {!disableBookmarks ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              testId="topToolbarBookmarksButton"
              className={
                bookmarksButtonClassName +
                ' rc-flex rc-gap-5 rc-items-center rc-justify-start rc-w-full'
              }
              onClick={() => {
                setShowTopToolbarPopover(false);
                onBookmarks();
              }}
              variant="light"
            >
              <MdBookmark className="rc-text-[18px] lg:rc-text-[28px]" />
              <span className="rc-block md:rc-hidden rc-text-xs">
                Bookmarks
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="rc-hidden md:rc-block">
            Bookmarks
          </TooltipContent>
        </Tooltip>
      ) : null}

      {!disableAnnotations ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              testId="topToolbarAnnotationsSidebarButton"
              className={
                annotationButtonClassName +
                ' rc-flex rc-gap-5 rc-items-center rc-justify-start rc-w-full'
              }
              onClick={() => {
                setShowTopToolbarPopover(false);
                onAnnotations();
              }}
              variant="light"
            >
              <MdChat className="rc-text-[18px] lg:rc-text-[28px]" />
              <span className="rc-block md:rc-hidden rc-text-xs">
                Annotations
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="rc-hidden md:rc-block">
            Annotations
          </TooltipContent>
        </Tooltip>
      ) : null}

      {onInfo ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                setShowTopToolbarPopover(false);
                onInfo();
              }}
              variant="light"
            >
              <FontAwesomeIcon icon={'info'} />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="rc-hidden md:rc-block">
            Info
          </TooltipContent>
        </Tooltip>
      ) : null}

      {onPrint ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              testId="topToolbarPrintButton"
              onClick={() => {
                setShowTopToolbarPopover(false);
                onPrint();
              }}
              variant="light"
              className="rc-flex rc-gap-5 rc-items-center rc-justify-start rc-w-full"
            >
              <MdPrint className="rc-text-[18px] lg:rc-text-[28px]" />
              <span className="rc-block md:rc-hidden rc-text-xs">Print</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="rc-hidden md:rc-block">
            Print
          </TooltipContent>
        </Tooltip>
      ) : null}

      {!disableSearch ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              testId="btnSearchTopToolbar"
              className={
                searchButtonClassName +
                ' rc-w-full rc-flex rc-gap-5 rc-items-center rc-justify-start'
              }
              onClick={() => {
                trackEvent('Webapp/TopToolbarSearchClicked', {
                  bookTitle: title,
                  bookId: bookId,
                });
                setShowTopToolbarPopover(false);
                onSearchOpen();
              }}
              variant="light"
            >
              <MdSearch className="rc-text-[18px] lg:rc-text-[28px]" />
              <span className="rc-block md:rc-hidden rc-text-xs">Search</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="rc-hidden md:rc-block">
            Search
          </TooltipContent>
        </Tooltip>
      ) : null}
    </>
  );

  const isLargeScreen = useMediaQuery('(min-width: 768px)');

  return (
    <ToolbarContainer
      className={C(
        customTextSelection ? 'iosTextSelectNone textSelectNone' : '',
        'rc-flex-shrink-0',
        !!currentlyDrawing && 'drawing-toolbar',
        className
      )}
      isOpen={isOpen || !!currentlyDrawing}
    >
      <div className="rc-flex rc-flex-col rc-w-full rc-justify-between rc-items-center rc-py-2 ">
        {!currentlyDrawing && !searchData.open ? (
          <div className="rc-flex rc-w-full rc-overflow-hidden rc-relative">
            <div className="rc-flex rc-flex-1 rc-w-full rc-h-full rc-items-center rc-justify-start">
              <Button to={'/'} variant="light" className="max-sm:rc-px-2">
                <MdArrowBack size={isLargeScreen ? 28 : 16} />
              </Button>
            </div>
            <div className="rc-flex rc-flex-[4_4_0%] rc-items-center rc-overflow-hidden rc-w-full rc-justify-center">
              <span className="rc-inline-block rc-truncate">{title}</span>
            </div>

            <div className="rc-flex rc-flex-1 rc-flex-col rc-w-full rc-items-end rc-justify-center">
              <div className="rc-flex rc-flex-1 rc-flex-row rc-w-auto rc-items-center rc-justify-end">
                <div className="rc-hidden max-md:rc-flex rc-flex-1 rc-flex-row">
                  {!loadingPages ? (
                    <Popover
                      open={showTopToolbarPopover}
                      onOpenChange={() => setShowTopToolbarPopover((s) => !s)}
                    >
                      <PopoverTrigger asChild>
                        <Button variant="light" icon>
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        collisionPadding={32}
                        style={{ zIndex: 210, width: 'fit-content' }}
                      >
                        <div className="rc-flex md:rc-hidden rc-gap-1 rc-items-start rc-flex-col">
                          {buttons}
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <LoadingSection width="2rem" />
                  )}
                </div>

                <div className="rc-flex rc-flex-1 rc-flex-row max-md:rc-hidden">
                  {!loadingPages ? buttons : <LoadingSection width="16rem" />}
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {searchData.open ? (
          <div
            className={C(
              'rc-flex rc-w-full rc-h-full rc-overflow-hidden',
              searchPanelClassName
            )}
          >
            <SearchBar />
          </div>
        ) : null}
        {currentlyDrawing ? (
          <DrawToolbar
            disableDrawing={disableDraw}
            disableHTML={disableHTML}
            disableMedia={disableMedia}
            bookId={bookId}
            authorId={authorId}
            addAnnotation={addAnnotation}
            pageNumber={pageNumber}
            onDiscard={onDiscardDrawing}
            tool={tool}
            onToolChange={onToolChange}
            confirmDiscard={confirmDiscard}
          />
        ) : (
          <div />
        )}
        {immersiveReaderPopup}
      </div>
    </ToolbarContainer>
  );
};
